// @flow
import React, { type Node } from 'react';
import moment from 'moment';
import { compose, pure } from 'recompose';
import { connect } from 'react-redux';
import { hot } from 'react-hot-loader';
import { Col, Grid, Row } from 'angieslist-react-components';
import { get, includes, isUndefined, isNull } from 'lodash';
import Loader from 'angieslist-app-platform/lib/browser/Loader';
import Experiment from 'angieslist-app-platform/lib/common/experiment/v3/Experiment';
import cookieUtils from 'angieslist-app-platform/lib/common/utils/cookieUtils';
import DelinquencyAlert from 'angieslist-office-app-common/lib/v1/header/DelinquencyAlert';
import AdvertiseWithUs from 'angieslist-office-app-common/lib/v1/header/AdvertiseWithUs';
import BusinessInfoPanel from './BusinessInfoPanel';
import type { UserState } from '../common/user/userReducer';
import type { ServiceProvider, ServiceProviderCacheState } from '../types';
import serviceProviderActions from '../common/serviceProvider/serviceProviderActions';
import * as advertiseActions from '../common/advertise/advertiseActions';
import './globalNav.scss';
import AdvertiseClaimDiscount from './AdvertiseClaimDiscount';
import { IntercomClient } from './IntercomClient';
import type { AdvertiseReducerState } from '../common/advertise/advertiseReducer';

type Props = {
  serviceProviderId: string,
  // Redux state
  serviceProviderCache: ServiceProviderCacheState,
  user: UserState,
  experiment: any,
  advertise: AdvertiseReducerState,
  // Redux actions
  getServiceProvider: typeof serviceProviderActions.getServiceProvider,
  updateServiceProviderId: typeof serviceProviderActions.updateServiceProviderId,
  experiment: any,
  advertiseRequest: typeof advertiseActions.advertiseRequest,
  closeAdvertiseAlert: typeof advertiseActions.closeAdvertiseAlert,
  contactCountByTopCategory: typeof advertiseActions.contactCountByTopCategory,
  location: any,
  config: any,
  browser: any,
};

export class GlobalNavComponent extends React.Component<Props> {
  componentDidMount() {
    const {
      getServiceProvider,
      serviceProviderId,
      updateServiceProviderId,
      contactCountByTopCategory,
    } = this.props;

    getServiceProvider(serviceProviderId);
    updateServiceProviderId(serviceProviderId);

    /* use start of day so that we can effectively cache */
    const startOfDay = moment().startOf('day');
    const endTime = startOfDay.toISOString();
    const startTime = startOfDay.subtract(90, 'days').toISOString();
    contactCountByTopCategory(serviceProviderId, startTime, endTime);
  }

  componentDidUpdate() {
    const {
      serviceProviderId,
      serviceProviderCache,
    } = this.props;

    const daysUntilLockout = get(serviceProviderCache[serviceProviderId], 'delinquency.daysUntilLockout');
    const delinquentCookie = cookieUtils.getCookie(`delinquent_alert_viewed-${serviceProviderId}`);

    const isAdvertiser = get(serviceProviderCache[serviceProviderId], 'isAdvertiser', false);
    if (!!isAdvertiser && !isUndefined(daysUntilLockout)) {
      if (daysUntilLockout < 1) {
        this.redirectSpToPaymentPage(serviceProviderId, true);
      } else if (!delinquentCookie) {
        this.redirectSpToPaymentPage(serviceProviderId, false);
      }
    }
  }

  redirectSpToPaymentPage: ((spid: string, isLockout: boolean) => void) =
   (spid: string, isLockout: boolean) => {
     const paymentPageHref = `/app/statements/${spid}/preferences/advertising`;
     if (window.location.pathname !== paymentPageHref) {
       if (isLockout) {
         window.location.replace(paymentPageHref);
       } else {
         window.location.href = paymentPageHref;
       }
     }
   }

  props: Props;

  render(): Node {
    const {
      serviceProviderId,
      user: {
        userInfo: {
          response: userResponse,
        },
      },
      advertise: {
        advertiseRequest: {
          isLoading: advertiseIsLoading,
          response: advertiseResponse,
          error: advertiseError,
        },
        contactCountByTopCategory: {
          isLoading: contactCountByTopCategoryIsLoading,
          response: contactCountByTopCategoryResponse,
          error: contactCountByTopCategoryError,
        },
      },
      serviceProviderCache,
      experiment,
      advertiseRequest,
      closeAdvertiseAlert,
      location,
      user,
    } = this.props;

    const { entitlements = [] } = userResponse || {};
    const isMultiSpid = entitlements.filter(x => x.scope === 'ServiceProvider').length > 1;
    const serviceProvider: ServiceProvider = serviceProviderCache[serviceProviderId] || {
      id: Number(serviceProviderId),
      name: '',
    };
    const { delinquency, intercomUserHash } = serviceProvider;
    const daysUntilLockout = get(delinquency, 'daysUntilLockout');
    const pathname = get(location, 'pathname', '');
    const pageBackgroundColor =
      includes(pathname, 'performanceFAQ') ||
      includes(pathname, 'messages')
        ? 'gray-background' : 'white-background';
    const showGlobalDelinquentAlert = !isUndefined(daysUntilLockout)
      && !includes(pathname, 'preferences/advertising')
      && !!serviceProvider.isAdvertiser;

    const userId = get(user, 'userInfo.response.userId');
    const userFirstName = get(user, 'userInfo.response.firstName', '');
    const userLastName = get(user, 'userInfo.response.lastName', '');
    const userEmail = get(user, 'userInfo.response.email', '');
    const userName = `${userFirstName} ${userLastName}`;

    const intercomAppId = get(this.props.config, 'intercomAppId');

    const showIntercom = serviceProvider.isAdvertiser && intercomAppId && intercomUserHash && !includes(pathname, 'preferences/advertising') && !includes(pathname, 'payment');
    const isAdvertiseClaimDiscount = get(experiment, 'experiments.advertiseClaimDiscount.enabled', false);

    return (
      <div>
        <div id="global-nav">
          <If condition={!!serviceProvider.isAdvertiser}>
            <Experiment name="intercom" />
          </If>
          <If condition={!serviceProvider.isAdvertiser}>
            <Experiment name="advertiseClaimDiscount" />
          </If>
          <If condition={showIntercom}>
            <IntercomClient
              appId={intercomAppId}
              userId={userId}
              userName={userName}
              userHash={intercomUserHash}
              email={userEmail}
              serviceProviderId={serviceProviderId}
              serviceProviderName={serviceProvider.name}
              browser={this.props.browser}
            />
          </If>
          <Grid>
            <Row>
              <Col xs={12}>
                <BusinessInfoPanel
                  serviceProvider={serviceProvider}
                  activityLocation="Auth Header"
                  showBusinessListLink={isMultiSpid}
                  entitlements={entitlements}
                />
              </Col>
            </Row>
          </Grid>
        </div>
        <If condition={showGlobalDelinquentAlert}>
          <div id="global-alert" className={pageBackgroundColor}>
            <Grid>
              <Row>
                <Col xs={12}>
                  <DelinquencyAlert
                    delinquency={delinquency}
                    spId={serviceProviderId}
                  />
                </Col>
              </Row>
            </Grid>
          </div>
        </If>
        <If condition={!serviceProvider.isAdvertiser}>
          <div id="global-advertise-with-us" className={pageBackgroundColor}>
            <Grid>
              <Row>
                <Col xs={12}>
                  <Loader
                    loading={
                      !contactCountByTopCategoryError &&
                      (contactCountByTopCategoryIsLoading ||
                      isNull(contactCountByTopCategoryResponse))
                    }
                    element={
                      <Row className="category-row text-center" style={{ marginTop: '1em' }}>
                        <i className="icon icon-load icon-spin icon-3x text-primary" />
                      </Row>}
                  >
                    <Choose>
                      <When condition={isAdvertiseClaimDiscount}>
                        <AdvertiseClaimDiscount
                          advertiseRequest={advertiseRequest}
                          advertiseRequestWasSuccessful={!advertiseIsLoading && advertiseResponse}
                          advertiseRequestError={advertiseError}
                          advertiseRequestPending={advertiseIsLoading}
                          closeAlert={closeAdvertiseAlert}
                          activityLocation="Global Nav"
                        />
                      </When>
                      <Otherwise>
                        <AdvertiseWithUs
                          advertiseRequest={advertiseRequest}
                          advertiseRequestWasSuccessful={!advertiseIsLoading && advertiseResponse}
                          advertiseRequestError={advertiseError}
                          advertiseRequestPending={advertiseIsLoading}
                          contactCountByTopCategoryResponse={contactCountByTopCategoryResponse}
                          contactCountByTopCategoryError={contactCountByTopCategoryError}
                          contactCountByTopCategoryPending={contactCountByTopCategoryIsLoading}
                          closeAlert={closeAdvertiseAlert}
                          activityLocation="Global Nav"
                          serviceProviderId={serviceProviderId}
                          userId={userId}
                        />
                      </Otherwise>
                    </Choose>
                  </Loader>
                </Col>
              </Row>
            </Grid>
          </div>
        </If>
      </div>
    );
  }
}

export default (hot(module)(compose(
  connect(
    state => ({
      serviceProviderCache: state.serviceProviderCache,
      isAuthenticated: !!state.auth.isAuthenticated,
      user: state.user,
      appName: state.config.appName,
      experiment: state.experimentV2,
      advertise: state.advertise,
      config: state.config,
      browser: state.browser,
    }),
    {
      ...serviceProviderActions,
      ...advertiseActions,
    },
  ),
  pure,
)(GlobalNavComponent)): any);



// WEBPACK FOOTER //
// ./src/app/browser/GlobalNav.js